import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { sprayService, SprayMachine } from '../../services/sprayService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import PageHeader from '../shell/PageHeader';
import { DataTable, FilterBar } from '../patterns';
import CalibrationStatusBadge from './CalibrationStatusBadge';

interface Props {
  onAdd?: () => void;
  onEdit?: (machine: SprayMachine) => void;
  onDelete?: (id: string) => void;
}

const SprayMachineList: React.FC<Props> = ({ onAdd, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [machines, setMachines] = useState<SprayMachine[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.machines.title') },
  ]);

  useEffect(() => {
    sprayService.getMachines()
      .then(setMachines)
      .catch(() => setError(t('spray.machines.loadError')));
    // Mount-only fetch: t is treated as stable (we only use it for the error message)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('spray.machines.confirmDelete'))) return;
    try {
      await sprayService.deleteMachine(id);
      setError('');
      setMachines(prev => prev.filter(m => m._id !== id));
      onDelete?.(id);
    } catch {
      setError(t('spray.machines.saveError'));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return machines;
    return machines.filter(m =>
      m.name.toLowerCase().includes(q) ||
      (m.tractorName ?? '').toLowerCase().includes(q)
    );
  }, [machines, search]);

  return (
    <div data-testid="spray-machine-list">
      <PageHeader
        title={t('spray.machines.title')}
        meta={t('spray.machines.metaCount', { count: machines.length, defaultValue: '{{count}} machines' })}
        actions={
          <button
            type="button"
            className="pat-btn pat-btn--primary"
            onClick={() => onAdd?.()}
          >
            <i className="bi bi-plus-lg" aria-hidden="true" /> {t('spray.pageHeader.actions.newMachine')}
          </button>
        }
      />

      {error && <Alert variant="danger" role="alert" className="mb-3">{error}</Alert>}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('spray.machines.searchPlaceholder')}
      />

      <DataTable<SprayMachine>
        rows={filtered}
        rowKey={(m) => m._id}
        columns={[
          { key: 'name',        label: t('spray.machines.name') },
          { key: 'tractorName', label: t('spray.machines.tractorName'), render: (m) => m.tractorName ?? '—' },
          {
            key: 'calibrationStatus',
            label: t('spray.machines.calibrationStatus.header'),
            render: (m) => <CalibrationStatusBadge machine={m} />,
          },
          {
            key: 'actions',
            label: '',
            align: 'right',
            render: (m) => (
              <span className="d-inline-flex gap-2">
                <button
                  type="button"
                  className="pat-btn pat-btn--subtle"
                  onClick={() => onEdit?.(m)}
                >
                  {t('common.edit')}
                </button>
                <button
                  type="button"
                  className="pat-btn pat-btn--danger"
                  onClick={() => handleDelete(m._id)}
                >
                  {t('common.delete')}
                </button>
              </span>
            ),
          },
        ]}
        emptyState={<div className="p-4 text-muted">{t('spray.machines.noMachines')}</div>}
      />
    </div>
  );
};

export default SprayMachineList;
